import React from "react";
import { Link } from "react-router-dom";
import { Cookie, ArrowLeft, Shield } from "lucide-react";

/**
 * Public cookie policy — mirrors /app/COOKIES.md as the canonical inventory
 * shown to visitors. Linked from the Secure Privacy banner (via footer) and
 * from the privacy policy.
 *
 * Route: /cookies
 */
export default function Cookies() {
  const updated = "23 February 2026";

  return (
    <div data-testid="cookies-page" className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24 text-ink">
      <Link
        to="/"
        data-testid="cookies-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted_ink hover:text-terracotta"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to Folio
      </Link>

      <div className="flex items-center gap-3 mt-8 mb-4">
        <Cookie className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
        <div className="label-eyebrow">Cookie notice</div>
      </div>
      <h1 className="font-serif text-5xl md:text-6xl leading-tight">Cookie policy</h1>
      <p className="text-muted_ink mt-4 text-sm">Last updated: {updated}</p>

      <div className="mt-12 space-y-10 text-base leading-relaxed">
        <section>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">How we handle cookies</h2>
          <div className="text-muted_ink space-y-3">
            <p>
              A cookie is a small text file a website stores in your browser to remember something
              about your visit. Some are essential (e.g. keeping you signed in), others power analytics
              or advertising.
            </p>
            <p>
              At Folio, <strong>every non-essential cookie is denied by default</strong> under Google
              Consent Mode v2. Nothing that isn't strictly necessary is set until you tap{" "}
              <strong>Accept all</strong> on our Secure Privacy banner. You can change your mind
              any time using the <strong>Cookie preferences</strong> link in the site footer.
            </p>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">Cookies we set</h2>
          <p className="text-muted_ink mb-4">
            Below is our current cookie inventory. This list is updated whenever we add or remove a provider.
          </p>
          <div className="overflow-x-auto border border-edge">
            <table className="w-full text-sm">
              <thead className="bg-linen">
                <tr>
                  <th className="text-left p-3 font-serif font-normal text-ink">Name</th>
                  <th className="text-left p-3 font-serif font-normal text-ink">Provider</th>
                  <th className="text-left p-3 font-serif font-normal text-ink">Purpose</th>
                  <th className="text-left p-3 font-serif font-normal text-ink">Duration</th>
                  <th className="text-left p-3 font-serif font-normal text-ink">Category</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r.name} className={i % 2 ? "bg-paper" : ""}>
                    <td className="p-3 align-top border-t border-edge"><code className="text-xs">{r.name}</code></td>
                    <td className="p-3 align-top border-t border-edge">{r.provider}</td>
                    <td className="p-3 align-top border-t border-edge">{r.purpose}</td>
                    <td className="p-3 align-top border-t border-edge">{r.duration}</td>
                    <td className="p-3 align-top border-t border-edge">
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${categoryClass(r.category)}`}>
                        {r.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">Local storage we use (not cookies)</h2>
          <p className="text-muted_ink mb-4">
            These live in your browser only, are never sent to a server, and are strictly necessary
            to run the app — so they don't require consent under Art. 5(3) ePrivacy.
          </p>
          <div className="overflow-x-auto border border-edge">
            <table className="w-full text-sm">
              <thead className="bg-linen">
                <tr>
                  <th className="text-left p-3 font-serif font-normal text-ink">Key</th>
                  <th className="text-left p-3 font-serif font-normal text-ink">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {LOCAL_STORAGE.map((r, i) => (
                  <tr key={r.key} className={i % 2 ? "bg-paper" : ""}>
                    <td className="p-3 align-top border-t border-edge"><code className="text-xs">{r.key}</code></td>
                    <td className="p-3 align-top border-t border-edge">{r.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">Changing or withdrawing consent</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted_ink">
            <li>Click <strong>Cookie preferences</strong> in the footer of any page.</li>
            <li>Or clear your site cookies + localStorage — the banner will re-appear on your next visit.</li>
            <li>Or write to <a href="mailto:privacy@folio.llc" className="underline hover:text-terracotta">privacy@folio.llc</a>.</li>
          </ul>
          <p className="text-muted_ink mt-4">
            Rejection is <strong>as easy as acceptance</strong> — one click on the banner (no dark patterns, no pre-ticked boxes).
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">Related</h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/privacy"
              data-testid="cookies-to-privacy"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-edge px-4 py-3 hover:border-terracotta hover:text-terracotta"
            >
              <Shield className="w-3.5 h-3.5" strokeWidth={1.5} /> Privacy policy
            </Link>
            <button
              type="button"
              data-testid="cookies-open-banner"
              onClick={() => {
                if (typeof window.SPShowCookieBanner === "function") window.SPShowCookieBanner();
                else if (window.SecurePrivacy && typeof window.SecurePrivacy.showBanner === "function") window.SecurePrivacy.showBanner();
                else {
                  if (typeof window.allConsentDenied === "function") window.allConsentDenied();
                  try {
                    localStorage.removeItem("SP_CONSENT_STATUS");
                    localStorage.removeItem("folio_consent");
                  } catch (err) {
                    console.warn("[cookies] could not clear consent keys:", err);
                  }
                  window.location.reload();
                }
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-edge px-4 py-3 hover:border-terracotta hover:text-terracotta"
            >
              <Cookie className="w-3.5 h-3.5" strokeWidth={1.5} /> Cookie preferences
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function categoryClass(c) {
  if (c === "Strictly necessary") return "border-sage text-sage";
  if (c === "Analytics") return "border-terracotta text-terracotta";
  return "border-edge text-muted_ink";
}

const ROWS = [
  {
    name: "_ga",
    provider: "Google Analytics 4",
    purpose: "Distinguish unique visitors so we can measure how the site is used.",
    duration: "2 years",
    category: "Analytics",
  },
  {
    name: "_ga_F1X4G80YGJ",
    provider: "Google Analytics 4",
    purpose: "Persist session state for the folio.llc GA property.",
    duration: "2 years",
    category: "Analytics",
  },
  {
    name: "SP_CONSENT_STATUS",
    provider: "Secure Privacy",
    purpose: "Store your consent choice so we don't ask again on every page.",
    duration: "12 months",
    category: "Strictly necessary",
  },
];

const LOCAL_STORAGE = [
  { key: "folio.token",           purpose: "Your JWT session token so you stay signed in." },
  { key: "folio.cart",            purpose: "The books in your cart before checkout." },
  { key: "folio_theme",           purpose: "Light or dark mode preference." },
  { key: "folio_theme_prompted",  purpose: "Whether we've asked you about dark mode already." },
  { key: "folio_consent",         purpose: "Mirror of your consent state used to restore defaults instantly on reload." },
  { key: "folio_subscribed",      purpose: "Local flag so we don't re-show the newsletter form after you subscribe." },
  { key: "folio_feedback_dismissed", purpose: "Whether you've dismissed the feedback tab." },
];
